import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Settings, Upload, Palette, Save } from "lucide-react";
import { toast } from "sonner";

const BrandSettings = ({ onClose }) => {
  const [settings, setSettings] = useState({
    company_name: localStorage.getItem('brand_company_name') || "",
    logo_url: localStorage.getItem('brand_logo') || "",
    primary_color: localStorage.getItem('brand_primary_color') || "#8b5cf6",
    secondary_color: localStorage.getItem('brand_secondary_color') || "#ec4899",
  });

  const handleSave = () => {
    // Save to localStorage (in real app, this would go to backend)
    localStorage.setItem('brand_company_name', settings.company_name);
    localStorage.setItem('brand_logo', settings.logo_url);
    localStorage.setItem('brand_primary_color', settings.primary_color);
    localStorage.setItem('brand_secondary_color', settings.secondary_color);
    
    toast.success("Configuración de marca guardada");
    onClose();
    
    // Reload to apply changes
    window.location.reload();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="glass max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-white flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Personalización de Marca
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Company Name */}
          <div>
            <Label className="text-gray-300 mb-2">Nombre de la Empresa</Label>
            <Input
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Ej: PRÓXIMA Corp"
              className="bg-gray-900/50"
            />
          </div>

          {/* Logo URL */}
          <div>
            <Label className="text-gray-300 mb-2">URL del Logo</Label>
            <Input
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://ejemplo.com/logo.png"
              className="bg-gray-900/50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Sube tu logo a un servicio como Imgur o usa una URL pública
            </p>
            {settings.logo_url && (
              <div className="mt-3 p-4 bg-gray-900/50 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Vista previa:</p>
                <img 
                  src={settings.logo_url} 
                  alt="Logo" 
                  className="h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    toast.error("Error al cargar logo");
                  }}
                />
              </div>
            )}
          </div>

          {/* Primary Color */}
          <div>
            <Label className="text-gray-300 mb-2">Color Primario</Label>
            <div className="flex gap-3 items-center">
              <Input
                type="color"
                value={settings.primary_color}
                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                className="w-20 h-10 bg-gray-900/50"
              />
              <Input
                value={settings.primary_color}
                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                placeholder="#8b5cf6"
                className="flex-1 bg-gray-900/50"
              />
            </div>
          </div>

          {/* Secondary Color */}
          <div>
            <Label className="text-gray-300 mb-2">Color Secundario</Label>
            <div className="flex gap-3 items-center">
              <Input
                type="color"
                value={settings.secondary_color}
                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                className="w-20 h-10 bg-gray-900/50"
              />
              <Input
                value={settings.secondary_color}
                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                placeholder="#ec4899"
                className="flex-1 bg-gray-900/50"
              />
            </div>
          </div>

          {/* Preview */}
          <Card 
            className="p-6 bg-gray-900/50 border-gray-700"
            style={{
              borderColor: settings.primary_color,
              borderWidth: '2px'
            }}
          >
            <h3 className="text-sm font-semibold text-gray-400 mb-3">Vista Previa</h3>
            <div className="flex items-center gap-4">
              {settings.logo_url && (
                <img 
                  src={settings.logo_url} 
                  alt="Logo preview" 
                  className="h-10 object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div>
                <h4 className="font-bold text-white" style={{ color: settings.primary_color }}>
                  {settings.company_name || "Tu Empresa"}
                </h4>
                <p className="text-sm" style={{ color: settings.secondary_color }}>
                  Reporte de Impacto Mensual
                </p>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-gradient-to-r from-purple-500 to-pink-500"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar Configuración
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BrandSettings;
